import { useQuery } from '@tanstack/react-query';
import { get } from '../../lib/api';

/**
 * Live catalogue health. This is the running version of the audit that found
 * 58% of variants missing barcodes before the NAV import. Re-run it any time to
 * track remediation — and note the framing: only IN-STOCK items without a
 * barcode actually block trading.
 */
export default function DataQuality() {
  const dq = useQuery({ queryKey: ['dq'], queryFn: () => get<any>('/api/v1/reports/data-quality') });
  const d = dq.data;

  return (
    <div className="p-6 space-y-5 max-w-4xl">
      <div>
        <h1 className="text-xl font-semibold">Catalogue data quality</h1>
        <p className="text-sm text-gray-400 mt-1">
          A POS is only as good as its item master. Barcode scanning is the single
          most important field at the till.
        </p>
      </div>

      {dq.isLoading && <div className="text-sm text-gray-500">Checking…</div>}
      {dq.isError && <div className="text-sm text-red-300">Could not run the check</div>}

      {d && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <Card label="Variants" value={String(d.variants)} />
            <Card label="Missing barcode" value={`${d.missingBarcode.pct}%`}
              sub={`${d.missingBarcode.count} variants`}
              tone={d.missingBarcode.pct > 20 ? 'warn' : 'ok'} />
            <Card label="Zero price" value={String(d.zeroPrice.count)}
              sub={`${d.zeroPrice.pct}% — cannot be sold`}
              tone={d.zeroPrice.count > 0 ? 'warn' : 'ok'} />
            <Card label="Duplicate SKUs" value={String(d.duplicateSkus)}
              tone={d.duplicateSkus > 0 ? 'warn' : 'ok'} />
          </div>

          <div className={`card p-5 border-l-4 ${
            d.inStockWithoutBarcode.count === 0 ? 'border-l-emerald-500' : 'border-l-amber-500'
          }`}>
            <div className="flex items-baseline justify-between">
              <div className="font-medium">Sellable stock that cannot be scanned</div>
              <span className={`money text-3xl font-bold ${
                d.inStockWithoutBarcode.count === 0 ? 'text-emerald-400' : 'text-amber-300'
              }`}>
                {d.inStockWithoutBarcode.count}
              </span>
            </div>
            <p className="text-sm text-gray-400 mt-1">{d.note}</p>

            {d.inStockWithoutBarcode.count > 0 && (
              <div className="mt-3 max-h-64 overflow-y-auto card divide-y divide-gray-800 text-xs">
                {d.inStockWithoutBarcode.items.map((it: any, i: number) => (
                  <div key={i} className="px-3 py-1.5 flex justify-between">
                    <span className="font-mono">{it.sku || <span className="text-red-400">(blank SKU)</span>}</span>
                    <span className="text-gray-400">{it.storeCode} · qty {it.onHand}</span>
                  </div>
                ))}
              </div>
            )}

            <div className="text-[11px] text-gray-500 mt-3 leading-relaxed">
              Fix these in Products by clicking "allocate EAN-13", which takes the next code
              from the FEMI9 8000001xxxxxx range and computes a valid check digit. Everything
              with zero stock can be tagged when it is next received.
            </div>
          </div>

          <div className="card p-5">
            <div className="label">How barcode coverage was improved</div>
            <div className="text-sm text-gray-300 space-y-1.5">
              <p>
                The initial Shopify import gave <strong>41.7%</strong> barcode coverage —
                4,237 of 7,262 variants had none, and 475 carried the literal string "N/A",
                which is worse than blank because it is duplicated across thousands of rows.
              </p>
              <p>
                Dynamics NAV holds 69,786 barcode records. Joining on
                <code className="mx-1 px-1 bg-gray-800 rounded text-xs">Item_No + "-" + Variant_Code</code>
                filled <strong>3,190</strong> of them, taking coverage to <strong>85.6%</strong>.
              </p>
              <p className="text-emerald-300">
                Validation: across 3,024 variants where both Shopify and NAV held a barcode,
                they agreed on <strong>all 3,024</strong> — zero conflicts. That is what made
                the import safe to apply rather than a guess.
              </p>
              <p className="text-gray-400 text-xs">
                Re-run with <code className="px-1 bg-gray-800 rounded">npm run nav:barcodes -- --apply</code>.
                It never overwrites an existing barcode and never creates a duplicate.
              </p>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function Card({ label, value, sub, tone }: {
  label: string; value: string; sub?: string; tone?: 'ok' | 'warn';
}) {
  return (
    <div className={`card p-4 ${tone === 'warn' ? 'border-amber-800' : ''}`}>
      <div className="label">{label}</div>
      <div className={`money text-2xl font-bold ${
        tone === 'warn' ? 'text-amber-300' : tone === 'ok' ? 'text-emerald-300' : ''
      }`}>{value}</div>
      {sub && <div className="text-[11px] text-gray-500 mt-0.5">{sub}</div>}
    </div>
  );
}
